// src/pages/markets/components/TraderStatsTooltip.tsx
import React, { useState } from 'react';
import { Box, Typography, Skeleton, CircularProgress } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { Link } from 'react-router-dom';
import type { TraderProfile, TraderPosition } from '../api/traderProfileTypes';
import { TAG_COLORS } from '../api/sidebarTradeTypes';
import { formatVolume } from '../utils/format';
import type { Theme } from '@mui/material/styles';

interface TraderStatsTooltipProps {
    address: string;
    profile: TraderProfile | null;
    loading: boolean;
    refreshing: boolean;
    error: string | null;
    traderHref?: string;
}

function shortAddr(addr: string): string {
    if (addr.length <= 10) return addr;
    return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function formatPnl(value: number): string {
    const prefix = value >= 0 ? '+' : '';
    if (Math.abs(value) >= 1e6) return `${prefix}$${(value / 1e6).toFixed(1)}M`;
    if (Math.abs(value) >= 1e3) return `${prefix}$${(value / 1e3).toFixed(1)}K`;
    return `${prefix}$${value.toFixed(0)}`;
}

function PositionRow({
    pos,
    address,
    theme,
    pnlColor,
}: {
    pos: TraderPosition;
    address: string;
    theme: Theme;
    pnlColor: (v: number) => string;
}) {
    return (
        <Box
            component={pos.event_slug ? Link : 'div'}
            {...(pos.event_slug
                ? {
                      to: `/markets/event/${pos.event_slug}?markets=${pos.condition_id}&traders=${address}`,
                      onClick: (e: React.MouseEvent) => e.stopPropagation(),
                  }
                : {})}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                py: '3px',
                px: 0.75,
                mb: '2px',
                bgcolor: alpha(theme.palette.action.hover, 0.04),
                borderRadius: 0.5,
                textDecoration: 'none',
                color: 'inherit',
                cursor: pos.event_slug ? 'pointer' : 'default',
                '&:hover': pos.event_slug
                    ? { bgcolor: alpha(theme.palette.action.hover, 0.12) }
                    : {},
            }}
        >
            <Box
                component="span"
                sx={{
                    px: 0.5,
                    borderRadius: '2px',
                    fontSize: '8px',
                    fontWeight: 700,
                    flexShrink: 0,
                    color:
                        pos.side === 'YES'
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                    bgcolor: alpha(
                        pos.side === 'YES'
                            ? theme.palette.success.main
                            : theme.palette.error.main,
                        0.15
                    ),
                }}
            >
                {pos.side}
            </Box>
            <Typography
                sx={{
                    fontSize: '10px',
                    color: pos.event_slug ? 'primary.main' : 'text.secondary',
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {pos.market_question}
            </Typography>
            <Typography
                sx={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    flexShrink: 0,
                }}
            >
                {formatVolume(pos.size_usd)}
            </Typography>
            <Typography
                sx={{
                    fontSize: '10px',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    flexShrink: 0,
                    color: pnlColor(pos.pnl),
                }}
            >
                {formatPnl(pos.pnl)}
            </Typography>
        </Box>
    );
}

export function TraderStatsTooltip({
    address,
    profile,
    loading,
    refreshing,
    error,
    traderHref,
}: TraderStatsTooltipProps) {
    const theme = useTheme();
    const [expanded, setExpanded] = useState(false);
    const pnlColor = (v: number) =>
        v >= 0 ? theme.palette.success.main : theme.palette.error.main;

    // -- Loading state (no cached data) --
    if (loading) {
        return (
            <Box sx={{ width: 280, p: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        mb: 1,
                    }}
                >
                    {shortAddr(address)}
                </Typography>
                <Skeleton
                    variant="rectangular"
                    height={14}
                    sx={{ mb: 0.75, borderRadius: 0.5 }}
                />
                <Skeleton
                    variant="rectangular"
                    height={14}
                    width="70%"
                    sx={{ mb: 0.75, borderRadius: 0.5 }}
                />
                <Skeleton
                    variant="rectangular"
                    height={14}
                    width="85%"
                    sx={{ borderRadius: 0.5 }}
                />
            </Box>
        );
    }

    // -- Error state (no cached data) --
    if (error && !profile) {
        return (
            <Box sx={{ width: 280, p: 0.5 }}>
                <Typography
                    sx={{
                        fontSize: '12px',
                        fontFamily: 'monospace',
                        fontWeight: 600,
                        mb: 0.5,
                    }}
                >
                    {shortAddr(address)}
                </Typography>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    Could not load trader stats
                </Typography>
            </Box>
        );
    }

    if (!profile) return null;

    const tags = profile.tags ?? [];
    const displayName = profile.alias || shortAddr(profile.address);
    const MAX_COLLAPSED = 3;
    const MAX_EXPANDED = 10;

    return (
        <Box sx={{ width: 280, p: 0.5 }}>
            <Box>
                {/* Row 1: Address/alias + tags + refreshing spinner */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        mb: 1,
                        flexWrap: 'wrap',
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '12px',
                            fontFamily: 'monospace',
                            fontWeight: 600,
                        }}
                    >
                        {displayName}
                    </Typography>
                    {tags.map(tag => (
                        <Box
                            key={tag}
                            component="span"
                            sx={{
                                px: 0.75,
                                py: '1px',
                                borderRadius: '3px',
                                fontSize: '9px',
                                fontWeight: 600,
                                color:
                                    TAG_COLORS[tag] ??
                                    theme.palette.text.secondary,
                                bgcolor: alpha(
                                    TAG_COLORS[tag] ??
                                        theme.palette.text.secondary,
                                    0.15
                                ),
                            }}
                        >
                            {tag}
                        </Box>
                    ))}
                    {refreshing && (
                        <CircularProgress size={10} sx={{ ml: 'auto' }} />
                    )}
                </Box>

                {/* Row 2: Total P&L */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                        mb: 1,
                        pb: 1,
                        borderBottom: 1,
                        borderColor: alpha(theme.palette.divider, 0.15),
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '10px',
                            color: 'text.secondary',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                        }}
                    >
                        Total P&L
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography
                            sx={{
                                fontSize: '14px',
                                fontWeight: 700,
                                fontFamily: 'monospace',
                                color: pnlColor(profile.performance.total_pnl),
                            }}
                        >
                            {formatPnl(profile.performance.total_pnl)}
                        </Typography>
                    </Box>
                </Box>

                {/* Row 3: Win rate + trade count */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 0.75,
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            Win Rate
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                mt: '1px',
                            }}
                        >
                            {(profile.performance.win_rate * 100).toFixed(0)}%
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            Trades
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                mt: '1px',
                            }}
                        >
                            {profile.activity.total_trades.toLocaleString()}
                        </Typography>
                    </Box>
                </Box>

                {/* Row 4: Volume + avg size */}
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mb: 1,
                        pb: 1,
                        borderBottom: 1,
                        borderColor: alpha(theme.palette.divider, 0.15),
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            Volume
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                mt: '1px',
                            }}
                        >
                            {formatVolume(profile.activity.lifetime_volume_usd)}
                        </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography
                            sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                            }}
                        >
                            Avg Size
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '13px',
                                fontWeight: 600,
                                mt: '1px',
                            }}
                        >
                            {formatVolume(profile.activity.avg_size_usd)}
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    mt: 1,
                    pt: 0.75,
                    borderTop: 1,
                    borderColor: alpha(theme.palette.divider, 0.15),
                    textAlign: 'center',
                }}
            >
                {traderHref ? (
                    <Typography
                        component={Link}
                        to={traderHref}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        sx={{
                            fontSize: '9px',
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                        }}
                    >
                        See full profile →
                    </Typography>
                ) : (
                    <Typography
                        sx={{ fontSize: '9px', color: 'text.disabled' }}
                    >
                        See full profile
                    </Typography>
                )}
            </Box>
        </Box>
    );
}
